import { jsx } from "react/jsx-runtime";
import { warnOnce } from "@tldraw/editor";
import * as React from "react";
import { useAssetUrls } from "../../context/asset-urls.mjs";
import { DEFAULT_TRANSLATION } from "./defaultTranslation.mjs";
import { fetchTranslation } from "./translations.mjs";
const TranslationsContext = React.createContext(null);
function useCurrentTranslation() {
  const translations = React.useContext(TranslationsContext);
  if (!translations) {
    throw new Error("useCurrentTranslation must be used inside of <TldrawUiContextProvider />");
  }
  return translations;
}
function useMaybeCurrentTranslation() {
  return React.useContext(TranslationsContext);
}
function TldrawUiTranslationProvider({
  overrides,
  locale,
  children
}) {
  const getAssetUrl = useAssetUrls();
  const [currentTranslation, setCurrentTranslation] = React.useState(() => {
    if (overrides && overrides["en"]) {
      return {
        locale: "en",
        label: "English",
        dir: "ltr",
        messages: { ...DEFAULT_TRANSLATION, ...overrides["en"] }
      };
    }
    return {
      locale: "en",
      label: "English",
      dir: "ltr",
      messages: DEFAULT_TRANSLATION
    };
  });
  React.useEffect(() => {
    let isCancelled = false;
    async function loadTranslation() {
      const translation = await fetchTranslation(locale, getAssetUrl);
      if (translation && !isCancelled) {
        if (overrides && overrides[locale]) {
          setCurrentTranslation({
            ...translation,
            messages: { ...translation.messages, ...overrides[locale] }
          });
        } else {
          setCurrentTranslation(translation);
        }
      }
    }
    loadTranslation();
    return () => {
      isCancelled = true;
    };
  }, [getAssetUrl, locale, overrides]);
  return /* @__PURE__ */ jsx(TranslationsContext.Provider, { value: currentTranslation, children });
}
function useTranslation() {
  const translation = React.useContext(TranslationsContext);
  const messages = translation?.messages ?? DEFAULT_TRANSLATION;
  React.useEffect(() => {
    if (!translation?.messages) {
      warnOnce(
        "No translation messages found, falling back to default translation. Wrap your app in <TldrawUiContextProvider>, or in both <AssetUrlsProvider> and <TldrawUiTranslationProvider>, to provide translations."
      );
    }
  }, [translation?.messages]);
  return React.useCallback(
    function msg(id) {
      return messages[id] ?? id;
    },
    [messages]
  );
}
function useDirection() {
  const translation = useMaybeCurrentTranslation();
  return translation?.dir ?? "ltr";
}
function untranslated(string) {
  return string;
}
export {
  TldrawUiTranslationProvider,
  TranslationsContext,
  untranslated,
  useCurrentTranslation,
  useDirection,
  useMaybeCurrentTranslation,
  useTranslation
};
//# sourceMappingURL=useTranslation.mjs.map
